/**
 * Copyright 2024 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import cn from "classnames";

import { memo, ReactNode, RefObject, useEffect, useRef, useState } from "react";
import { useLiveAPIContext } from "../../contexts/LiveAPIContext";
import { UseMediaStreamResult } from "../../hooks/use-media-stream-mux";

import { useWebcam } from "../../hooks/use-webcam";
import { AudioRecorder } from "../../lib/audio-recorder";
import AudioPulse from "../audio-pulse/AudioPulse";
import "./control-tray.scss";

/**
 * Force audio output to loudspeaker on mobile devices
 * Mobile browsers often default to earpiece which is too quiet
 */
const forceAudioToLoudspeaker = async (audioElement?: HTMLAudioElement): Promise<void> => {
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  
  if (!isMobile) {
    console.log('[Audio] Not mobile - skipping loudspeaker forcing');
    return;
  }

  console.log('[Audio] 🔊 Forcing audio to loudspeaker on mobile device');

  try {
    // Method 1: Use setSinkId API (Chrome/Edge on Android)
    if (audioElement && 'setSinkId' in audioElement) {
      try {
        await (audioElement as any).setSinkId('');
        console.log('[Audio] ✅ setSinkId successful');
      } catch (setSinkError) {
        console.log('[Audio] setSinkId not available or failed:', setSinkError);
      }
    }

    // Method 2: Create hidden audio element to lock audio routing to loudspeaker
    // This works by creating a brief, silent audio tone that forces the system
    // to use the loudspeaker instead of the earpiece
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    
    // Resume context if suspended (required on mobile)
    if (audioContext.state === 'suspended') {
      await audioContext.resume();
    }
    
    // Create a very brief, silent oscillator to lock audio routing
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    // Set to silent (or barely audible)
    gainNode.gain.value = 0.01;
    
    // Connect: oscillator -> gain -> destination
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    // Play for a brief moment
    oscillator.start(0);
    
    // Clean up after 1 second
    setTimeout(() => {
      oscillator.stop();
      audioContext.close();
      console.log('[Audio] ✅ Audio routing anchor cleaned up');
    }, 1000);
    
    console.log('[Audio] ✅ Loudspeaker forcing completed');
    
  } catch (error) {
    console.error('[Audio] ❌ Error forcing loudspeaker:', error);
  }
};


export type ControlTrayProps = {
  videoRef: RefObject<HTMLVideoElement>;
  children?: ReactNode;
  supportsVideo: boolean;
  onVideoStreamChange?: (stream: MediaStream | null) => void;
};

type MediaStreamButtonProps = {
  isStreaming: boolean;
  onIcon: string;
  offIcon: string;
  start: () => Promise<any>;
  stop: () => any;
};

/**
 * button used for triggering webcam or screen-capture
 */
const MediaStreamButton = memo(
  ({ isStreaming, onIcon, offIcon, start, stop }: MediaStreamButtonProps) =>
    isStreaming ? (
      <button className="action-button" onClick={stop}>
        <span className="material-symbols-outlined">{onIcon}</span>
      </button>
    ) : (
      <button className="action-button" onClick={start}>
        <span className="material-symbols-outlined">{offIcon}</span>
      </button>
    )
);

function ControlTray({
  videoRef,
  children,
  onVideoStreamChange = () => {},
  supportsVideo,
}: ControlTrayProps) {
  const webcam = useWebcam();
  const videoStreams = [webcam];
  const [activeVideoStream, setActiveVideoStream] =
    useState<MediaStream | null>(null);
  const [inVolume, setInVolume] = useState(0);
  const [audioRecorder] = useState(() => new AudioRecorder());
  const [muted, setMuted] = useState(false);
  const renderCanvasRef = useRef<HTMLCanvasElement>(null);
  const connectButtonRef = useRef<HTMLButtonElement>(null);

  const { client, connected, connect, disconnect, volume, audioStreamer } =
    useLiveAPIContext();

  useEffect(() => {
    if (!connected && connectButtonRef.current) {
      connectButtonRef.current.focus();
    }
  }, [connected]);

  // Force audio to loudspeaker when connected (mobile fix)
  useEffect(() => {
    if (connected) {
      console.log('[ControlTray] Connected - forcing audio to loudspeaker');
      forceAudioToLoudspeaker();
      
      // Also set output gain to maximum for mobile
      if (audioStreamer && audioStreamer.gainNode) {
        console.log('[ControlTray] Setting output gain to maximum');
        audioStreamer.gainNode.gain.setValueAtTime(1.0, audioStreamer.context.currentTime);
      }
    }
  }, [connected, audioStreamer]);

  // Update activeVideoStream when webcam stream changes (e.g., after camera switching)
  useEffect(() => {
    if (webcam.isStreaming && webcam.stream) {
      console.log('📹 Updating video element with new webcam stream after camera switch');
      setActiveVideoStream(webcam.stream);
      onVideoStreamChange(webcam.stream);
    } else if (!webcam.isStreaming) {
      console.log('📹 Clearing video element - webcam stopped');
      setActiveVideoStream(null);
      onVideoStreamChange(null);
    }
  }, [webcam.stream, webcam.isStreaming, onVideoStreamChange]);
  useEffect(() => {
    document.documentElement.style.setProperty(
      "--volume",
      `${Math.max(5, Math.min(inVolume * 200, 8))}px`
    );
  }, [inVolume]);

  useEffect(() => {
    const onData = (base64: string) => {
      client.sendRealtimeInput([
        {
          mimeType: "audio/pcm;rate=16000",
          data: base64,
        },
      ]);
    };
    if (connected && audioRecorder) {
      // Start audio recorder when connected
      audioRecorder.on("data", onData).on("volume", setInVolume).start();
      
      // Apply current mute state using gain control (doesn't stop stream)
      if (muted) {
        audioRecorder.muteMic();
      } else {
        audioRecorder.unmuteMic();
      }
    } else {
      // Stop when disconnected
      audioRecorder.stop();
    }
    return () => {
      audioRecorder.off("data", onData).off("volume", setInVolume);
    };
  }, [connected, client, audioRecorder]);

  // Handle muting separately using gain control (keeps stream active)
  useEffect(() => {
    if (connected && audioRecorder) {
      if (muted) {
        console.log('[ControlTray] Muting microphone via gain control');
        audioRecorder.muteMic();
      } else {
        console.log('[ControlTray] Unmuting microphone via gain control');
        audioRecorder.unmuteMic();
      }
    }
  }, [muted, connected, audioRecorder]);

  useEffect(() => {
    if (videoRef.current) {
      console.log('🎥 Setting video srcObject:', activeVideoStream ? 'New stream' : 'null');
      videoRef.current.srcObject = activeVideoStream;
      
      // Force video to load and play the new stream (important for Android)
      if (activeVideoStream) {
        videoRef.current.load();
        videoRef.current.play().catch((error) => {
          console.warn('📹 Video play failed (this is usually normal):', error);
        });
      }
    }

    let timeoutId = -1;

    function sendVideoFrame() {
      const video = videoRef.current;
      const canvas = renderCanvasRef.current;

      if (!video || !canvas) {
        return;
      }

      const ctx = canvas.getContext("2d")!;
      canvas.width = video.videoWidth * 0.25;
      canvas.height = video.videoHeight * 0.25;
      if (canvas.width + canvas.height > 0) {
        ctx.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);
        const base64 = canvas.toDataURL("image/jpeg", 1.0);
        const data = base64.slice(base64.indexOf(",") + 1, Infinity);
        client.sendRealtimeInput([{ mimeType: "image/jpeg", data }]);
      }
      if (connected) {
        timeoutId = window.setTimeout(sendVideoFrame, 1000 / 0.5);
      }
    }
    if (connected && activeVideoStream !== null) {
      requestAnimationFrame(sendVideoFrame);
    }
    return () => {
      clearTimeout(timeoutId);
    };
  }, [connected, activeVideoStream, client, videoRef]);

  //handler for swapping from one video-stream to the next
  const changeStreams = (next?: UseMediaStreamResult) => async () => {
    if (next) {
      const mediaStream = await next.start();
      setActiveVideoStream(mediaStream);
      onVideoStreamChange(mediaStream);
    } else {
      setActiveVideoStream(null);
      onVideoStreamChange(null);
    }

    videoStreams.filter((msr) => msr !== next).forEach((msr) => msr.stop());
  };

  return (
    <section className="control-tray">
      <canvas style={{ display: "none" }} ref={renderCanvasRef} />
      <nav className={cn("actions-nav", { disabled: !connected, hidden: !connected })}>
        <button
          className={cn("action-button mic-button")}
          onClick={async () => {
            setMuted(!muted);
            // Force audio routing after mute toggle (mobile fix)
            await forceAudioToLoudspeaker();
            // Ensure output gain is at maximum
            if (audioStreamer && audioStreamer.gainNode) {
              audioStreamer.gainNode.gain.setValueAtTime(1.0, audioStreamer.context.currentTime);
            }
          }}
        >
          {!muted ? (
            <span className="material-symbols-outlined filled">mic</span>
          ) : (
            <span className="material-symbols-outlined filled">mic_off</span>
          )}
        </button>

        {supportsVideo && (
          <>
            <MediaStreamButton
              isStreaming={webcam.isStreaming}
              start={changeStreams(webcam)}
              stop={changeStreams()}
              onIcon="videocam_off"
              offIcon="videocam"
            />
            {/* Camera switch button - only show when webcam is active and multiple cameras available */}
            {webcam.isStreaming && webcam.canSwitchCamera && (
              <button
                className={cn("action-button camera-switch", { 
                  switching: webcam.isSwitching 
                })}
                onClick={webcam.switchCamera}
                disabled={webcam.isSwitching}
                title={webcam.isSwitching 
                  ? 'Switching camera...' 
                  : `Switch to ${webcam.facingMode === 'user' ? 'back' : 'front'} camera`
                }
              >
                {webcam.isSwitching ? (
                  <span className="material-symbols-outlined rotating">
                    sync
                  </span>
                ) : (
                  <span className="material-symbols-outlined">
                    {webcam.facingMode === 'user' ? 'camera_rear' : 'camera_front'}
                  </span>
                )}
              </button>
            )}
          </>
        )}
        
        <div className="action-button no-action outlined">
          <AudioPulse volume={volume} active={connected} hover={false} />
        </div>
        
        {children}
      </nav>

      <div className={cn("connection-container", { connected })}>
        <div className="connection-button-container">
          <button
            ref={connectButtonRef}
            className={cn("action-button connect-toggle", { connected })}
            onClick={connected ? disconnect : connect}
          >
            {connected ? (
            <img src="/animationPause.gif" alt="" style={{width: '90px', height: '90px',zIndex: 1}} />
          ) : (
            <span className="material-symbols-outlined filled">play_arrow</span>
          )}
            {/* <span className="material-symbols-outlined filled">
              {connected ? "pause" : "play_arrow"}
            </span> */}
          </button>
        </div>
        <span className="text-indicator">Streaming</span>
      </div>
    </section>
  );
}

export default memo(ControlTray);
